import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ConfirmModal } from './ConfirmModal';
import type { ConfirmRequest } from './ConfirmModal';

/**
 * Remplaçant du confirm() natif (passation §6) : une seule modale de confirmation montée au
 * niveau de l'app, appelée comme une promesse.
 *
 *   const confirmAction = useConfirm();
 *   if (!(await confirmAction({ title: 'Supprimer le contact ?', description: 'La fiche de …', confirmLabel: 'Supprimer le contact' }))) return;
 *
 * Le flux du code appelant ne change pas : la ligne `if (!confirm(...)) return;` devient une
 * ligne équivalente, mais la question nomme ce qui va disparaître et le bouton porte le verbe.
 */
export type ConfirmOptions = Omit<ConfirmRequest, 'onConfirm'>;

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
    const [request, setRequest] = useState<ConfirmRequest | null>(null);
    const resolver = useRef<((ok: boolean) => void) | null>(null);

    const confirmAction = useCallback<ConfirmFn>((options) => new Promise<boolean>((resolve) => {
        // Une demande en attente est refusée si une nouvelle arrive (cas théorique).
        resolver.current?.(false);
        resolver.current = resolve;
        setRequest({ ...options, onConfirm: () => { resolver.current = null; resolve(true); } });
    }), []);

    const close = useCallback(() => {
        setRequest(null);
        resolver.current?.(false);
        resolver.current = null;
    }, []);

    const value = useMemo(() => confirmAction, [confirmAction]);
    return (
        <ConfirmContext.Provider value={value}>
            {children}
            <ConfirmModal request={request} onClose={close} />
        </ConfirmContext.Provider>
    );
}

export function useConfirm(): ConfirmFn {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error('useConfirm doit être utilisé sous ConfirmProvider');
    return ctx;
}
